import { forwardRef, useRef, useState } from 'react';

import { HorizontalContainer, TextInput } from '@american-autonomy/ag-ui';

import { TEST_ID } from './constants';
import type { Props } from './types';

import styles from './styles.module.scss';

const SearchInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
    const {
        className = '',
        debounceTime = 300,
        isDisabled = false,
        isEditable = true,
        isRequired = false,
        items = [],
        name = '',
        onFilter,
        onFilterResults,
        placeholder = '',
        testId = TEST_ID
    } = props;

    const [searchValue, setSearchValue] = useState('');
    const inputRef = useRef<HTMLInputElement | null>(null);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const handleAsyncFilter = async (searchTerm: string) => {
        await onFilter?.(searchTerm);
    };

    const handleChange = (newValue: string) => {
        setSearchValue(newValue);

        if (onFilter) {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }

            debounceTimeout.current = setTimeout(() => {
                handleAsyncFilter(newValue);
            }, debounceTime);

            return;
        }

        const lowercasedNewValue = newValue.toLowerCase();

        const filtered = items.filter(
            ({ label, value }) =>
                label.toLowerCase().includes(lowercasedNewValue) ||
                value.toLowerCase().includes(lowercasedNewValue)
        );

        onFilterResults?.(filtered);
    };

    return (
        <HorizontalContainer className={className} testId={testId}>
            <TextInput
                className={`${styles.textInput} ${className}`}
                isDisabled={isDisabled}
                isEditable={isEditable}
                isRequired={isRequired}
                name={name}
                onChange={handleChange}
                placeholder={placeholder}
                ref={ref || inputRef}
                value={searchValue}
            />
        </HorizontalContainer>
    );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;
